import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

// Type definitions for Umami tracking
interface UmamiWindow extends Window {
  umami?: {
    track: (eventName: string, eventData?: Record<string, unknown>) => void;
  };
}

declare const window: UmamiWindow;

// Track paths with timestamps to allow revisiting after a certain time period
interface TrackedPath {
  path: string;
  timestamp: number;
}

const trackedPaths: TrackedPath[] = [];
let isGlobalInitialized = false;

// Time in milliseconds after which a path should be tracked again (5 minutes)
const TRACK_AGAIN_AFTER = 5 * 60 * 1000;

// Debug flag - can be enabled/disabled here
const DEBUG = false; // Set to true temporarily for debugging

// Simple logging function
const log = (message: string, data?: any) => {
  if (DEBUG) {
    console.log(`[UmamiTracker] ${message}`, data || '');
  }
};

/**
 * Hook for tracking page views and events with Umami
 */
export const useUmamiTracker = () => {
  const location = useLocation();
  const isInitialMount = useRef(true);
  
  // Track page view when location changes
  useEffect(() => {
    // Initialize global tracking state on first mount
    if (!isGlobalInitialized) {
      isGlobalInitialized = true;
      log('Global tracking initialized');
      return;
    }
    
    if (isInitialMount.current) {
      isInitialMount.current = false;
      log('Initial component mount - skipping tracking');
      return;
    }
    
    const currentPath = location.pathname + location.search;
    const now = Date.now();
    
    log(`Path change detected: ${currentPath}`);
    
    // Clean up old tracked paths (older than TRACK_AGAIN_AFTER)
    const oldestValidTime = now - TRACK_AGAIN_AFTER;
    
    // Check if this path was tracked recently
    const trackedPathsIndex = trackedPaths.findIndex(
      entry => entry.path === currentPath && entry.timestamp > oldestValidTime
    );
    
    // Track if path hasn't been tracked recently
    if (window.umami && trackedPathsIndex === -1) {
      trackedPaths.push({
        path: currentPath,
        timestamp: now
      });
      log(`Tracking path: ${currentPath}`);
      window.umami.track(currentPath);
    } else {
      log(`Skipping track for recently tracked path: ${currentPath}`);
    }
  }, [location.pathname, location.search]);

  /**
   * Track a custom event with Umami
   * @param eventName - Name of the event to track
   * @param eventData - Optional data to send with the event
   */
  const trackEvent = (eventName: string, eventData?: Record<string, unknown>) => {
    if (window.umami) {
      log(`Tracking event: ${eventName}`, eventData);
      window.umami.track?.(eventName, eventData || {});
    } else {
      log(`Umami not available for event: ${eventName}`);
    }
  };

  return { trackEvent };
}; 